import * as React from 'react';
import {styled, emotionStyled} from '@packages/utilities/styled';
import {VisuallyHidden} from '@packages/systems/spring';
import {type CSSProperties} from '@packages/utilities/css-properties-type';

const Asterisk = styled('span')(
  {color: 'red', marginLeft: 4},
  {
    noUserSelect: () => ({
      WebkitUserSelect: 'none',
    }),
  }
);

const Label = styled('label')(
  ({customLabelStyles, theme: {colors, typography}}) => ({
    color: colors.foreground,
    ...typography.finePrint,
    lineHeight: 2,

    // Allow custom label styles to override the default
    ...customLabelStyles,
  }),
  {
    noUserSelect: () => ({
      WebkitUserSelect: 'none',
    }),
  }
);

const LabelWrapper = emotionStyled('div')({
  display: 'flex',
});

/*
  Use LogicLabel for an accessible label that is linked to the corresponding input.

  Option 1: The input can be a child of the label. htmlFor is not needed.

    <LogicLabel label="My cool label" required>
      <TextInput />
    <LogicLabel>

  Note: if an ExpressionEditor implementation renders one field only, you can wrap a LogicLabel around it.
    <LogicLabel label="My cool label" required>
      <ExpressionEditor dispatch={dispatch} expression={expr} />
    <LogicLabel>

  Option 2: If the input is not a child of the label, use htmlFor with the input's id.

    <LogicLabel label="My cool label" htmlFor="text-input-id" required/>
    <TextInput id="text-input-id"/>
 */

export const LogicLabel = ({
  label,
  htmlFor,
  required,
  children,
  noUserSelect,
  customLabelStyles = {},
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  children?: React.ReactNode;
  noUserSelect?: boolean;
  customLabelStyles?: CSSProperties;
}) => {
  return children ? (
    <LabelWrapper>
      <Label
        style={{width: '100%'}}
        customLabelStyles={customLabelStyles}
        noUserSelect={noUserSelect}
      >
        {label}
        {required && (
          <>
            <Asterisk aria-hidden noUserSelect={noUserSelect}>
              *
            </Asterisk>
            <VisuallyHidden>required</VisuallyHidden>
          </>
        )}
        {children}
      </Label>
    </LabelWrapper>
  ) : (
    <LabelWrapper>
      <Label
        customLabelStyles={customLabelStyles}
        htmlFor={htmlFor}
        noUserSelect={noUserSelect}
      >
        {label} {required && <VisuallyHidden>required</VisuallyHidden>}
      </Label>
      {required && (
        <Asterisk aria-hidden noUserSelect={noUserSelect}>
          *
        </Asterisk>
      )}
    </LabelWrapper>
  );
};
